<template>
    <nav
        v-if="totalPages > 1"
        ref="paginationNav"
        class="sl-pagination"
        :aria-label="$t('lbl_EMPBE_SalesLead_Pagination_Aria')"
    >
        <button
            v-for="(item, idx) in pageItems"
            :key="`sl-pg-${idx}-${item}`"
            type="button"
            :class="paginationBtnClass(item)"
            :disabled="item === 'ellipsis'"
            :aria-current="typeof item === 'number' && item === currentPage ? 'page' : null"
            :aria-label="paginationAriaLabel(item)"
            @click="onItemClick(item)"
        >
            <template v-if="item === 'ellipsis'">...</template>
            <template v-else-if="item === 'prev'">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                    <path d="M11.5 3.5L6.5 9L11.5 14.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </template>
            <template v-else-if="item === 'next'">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                    <path d="M6.5 3.5L11.5 9L6.5 14.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </template>
            <template v-else>{{ item }}</template>
        </button>
    </nav>
</template>

<script>
const GAP_PX_DESKTOP = 8;
const GAP_PX_MOBILE = 6;
const ELLIPSIS_PX_DESKTOP = 28;
/** 与 .sl-pagination__btn--ellipsis min-width + padding 一致，略留余量防低估 */
const ELLIPSIS_PX_MOBILE = 32;
/** 估算与真实渲染之间的安全边距（px） */
const BUDGET_SAFETY_PX_MOBILE = 16;
const BUDGET_SAFETY_PX_DESKTOP = 8;

/**
 * 计算连续页码窗口：当前页尽量居中；省略号仅出现在页码后方（最多一个）。
 */
function computeSalesLeadPageWindow(currentPage, totalPages, maxNumericSlots) {
    const cur = Math.max(1, Number(currentPage) || 1);
    const total = Math.max(1, Number(totalPages) || 1);
    const win = Math.max(1, Math.min(total, Number(maxNumericSlots) || 1));

    if (total <= win) {
        return { start: 1, end: total, trailingEllipsis: false };
    }

    const half = Math.floor((win - 1) / 2);
    let start = cur - half;
    let end = start + win - 1;

    if (start < 1) {
        start = 1;
        end = win;
    } else if (end > total) {
        end = total;
        start = total - win + 1;
    }

    return {
        start,
        end,
        trailingEllipsis: end < total,
    };
}

/**
 * 分页条：‹ + 页码 + （至多一个 … 在页码后）+ ›
 * 不出现前置 …，不出现两个 …
 */
function buildSalesLeadPageItems(currentPage, totalPages, maxNumericSlots) {
    const cur = Math.max(1, Number(currentPage) || 1);
    const total = Math.max(1, Number(totalPages) || 1);
    const items = [];
    if (total <= 1) return items;

    if (cur > 1) items.push('prev');

    const { start, end, trailingEllipsis } = computeSalesLeadPageWindow(cur, total, maxNumericSlots);
    for (let p = start; p <= end; p += 1) items.push(p);
    if (trailingEllipsis) items.push('ellipsis');

    if (cur < total) items.push('next');

    return items;
}

/** 估算分页条占用宽度（px） */
function estimateSalesLeadPaginationWidth(
    items,
    btnSize,
    gapPx = GAP_PX_DESKTOP,
    ellipsisPx = ELLIPSIS_PX_DESKTOP,
) {
    if (!items.length) return 0;
    let w = 0;
    items.forEach((item, i) => {
        if (i > 0) w += gapPx;
        if (item === 'ellipsis') w += ellipsisPx;
        else w += btnSize;
    });
    return w;
}

/**
 * 按可用宽度二分求最多可展示的连续页码数（含箭头、后置 … 的真实占位）。
 */
function maxNumericSlotsForBudget(
    budget,
    btnSize,
    gapPx,
    currentPage,
    totalPages,
    ellipsisPx = ELLIPSIS_PX_DESKTOP,
) {
    const cur = Math.max(1, Number(currentPage) || 1);
    const total = Math.max(1, Number(totalPages) || 1);
    const budgetNum = Math.max(0, Number(budget) || 0);
    if (budgetNum <= 0 || total <= 1) return 1;

    let lo = 1;
    let hi = total;
    let best = 1;
    while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        const items = buildSalesLeadPageItems(cur, total, mid);
        const w = estimateSalesLeadPaginationWidth(items, btnSize, gapPx, ellipsisPx);
        if (w <= budgetNum) {
            best = mid;
            lo = mid + 1;
        } else {
            hi = mid - 1;
        }
    }
    return best;
}

function resolveSalesLeadPageItems(
    budget,
    btnSize,
    gapPx,
    currentPage,
    totalPages,
    ellipsisPx,
    safetyPx = 0,
) {
    const effective = Math.max(0, (Number(budget) || 0) - (Number(safetyPx) || 0));
    const maxWin = maxNumericSlotsForBudget(effective, btnSize, gapPx, currentPage, totalPages, ellipsisPx);
    return buildSalesLeadPageItems(currentPage, totalPages, maxWin);
}

export default {
    name: 'SalesLeadPagination',
    props: {
        currentPage: { type: Number, default: 1 },
        totalPages: { type: Number, default: 1 },
        isMobile: { type: Boolean, default: false },
    },
    data() {
        return {
            containerWidth: 0,
            /** 渲染后 scrollWidth 超出时下调页码数 */
            fitMaxWin: null,
        };
    },
    computed: {
        btnSizePx() {
            return this.isMobile ? 36 : 40;
        },
        gapPx() {
            return this.isMobile ? GAP_PX_MOBILE : GAP_PX_DESKTOP;
        },
        ellipsisPx() {
            return this.isMobile ? ELLIPSIS_PX_MOBILE : ELLIPSIS_PX_DESKTOP;
        },
        budgetSafetyPx() {
            return this.isMobile ? BUDGET_SAFETY_PX_MOBILE : BUDGET_SAFETY_PX_DESKTOP;
        },
        fallbackContainerWidth() {
            if (this.isMobile) {
                const vw = typeof window !== 'undefined' ? window.innerWidth : 360;
                /* container-fluid + .leftmenu padding(24×2) 保守扣减 */
                return Math.max(200, vw - 30 - 48);
            }
            return 352;
        },
        budgetWidth() {
            const raw = this.containerWidth > 0 ? this.containerWidth : this.fallbackContainerWidth;
            return Math.max(160, raw - this.budgetSafetyPx);
        },
        pageItems() {
            const cur = this.currentPage;
            const total = this.totalPages;
            const btn = this.btnSizePx;
            const gap = this.gapPx;
            const ell = this.ellipsisPx;

            if (this.fitMaxWin != null) {
                return buildSalesLeadPageItems(cur, total, this.fitMaxWin);
            }

            return resolveSalesLeadPageItems(
                this.budgetWidth,
                btn,
                gap,
                cur,
                total,
                ell,
                0,
            );
        },
    },
    watch: {
        isMobile() {
            this.resetFit();
            this.$nextTick(() => {
                this.measureWidth();
                this.enforceFitAfterRender();
            });
        },
        currentPage() {
            this.resetFit();
            this.$nextTick(() => this.enforceFitAfterRender());
        },
        totalPages() {
            this.resetFit();
            this.$nextTick(() => this.enforceFitAfterRender());
        },
        containerWidth() {
            this.resetFit();
        },
    },
    mounted() {
        this.measureWidth();
        this.bindResizeObserver();
        window.addEventListener('resize', this.onWindowResize);
        this.$nextTick(() => this.enforceFitAfterRender());
    },
    updated() {
        this.$nextTick(() => this.enforceFitAfterRender());
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.onWindowResize);
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
    },
    methods: {
        resetFit() {
            this.fitMaxWin = null;
        },
        onWindowResize() {
            this.resetFit();
            this.measureWidth();
            this.$nextTick(() => this.enforceFitAfterRender());
        },
        measureWidth() {
            const nav = this.$refs.paginationNav;
            if (!nav) return;
            const w = nav.clientWidth > 0 ? nav.clientWidth : (nav.parentElement && nav.parentElement.clientWidth);
            if (w > 0 && w !== this.containerWidth) {
                this.containerWidth = w;
            }
        },
        /**
         * 用真实 DOM 宽度校验：超出则减少页码数直至不溢出。
         */
        enforceFitAfterRender() {
            const nav = this.$refs.paginationNav;
            if (!nav || this.totalPages <= 1) return;

            this.measureWidth();
            const available = nav.clientWidth;
            if (available <= 0) return;

            if (nav.scrollWidth <= available + 1) {
                return;
            }

            const cur = this.currentPage;
            const total = this.totalPages;
            const btn = this.btnSizePx;
            const gap = this.gapPx;
            const ell = this.ellipsisPx;
            const budget = Math.max(120, available - this.budgetSafetyPx);

            let maxWin = maxNumericSlotsForBudget(budget, btn, gap, cur, total, ell);
            while (maxWin > 1) {
                const items = buildSalesLeadPageItems(cur, total, maxWin);
                const est = estimateSalesLeadPaginationWidth(items, btn, gap, ell);
                if (est <= budget) break;
                maxWin -= 1;
            }

            if (this.fitMaxWin !== maxWin) {
                this.fitMaxWin = maxWin;
                this.$nextTick(() => {
                    const nav2 = this.$refs.paginationNav;
                    if (!nav2) return;
                    if (nav2.scrollWidth > nav2.clientWidth + 1 && maxWin > 1) {
                        this.fitMaxWin = Math.max(1, maxWin - 1);
                    }
                });
            }
        },
        bindResizeObserver() {
            if (typeof ResizeObserver === 'undefined') return;
            this._resizeObserver = new ResizeObserver(() => {
                this.resetFit();
                this.measureWidth();
                this.$nextTick(() => this.enforceFitAfterRender());
            });
            this.$nextTick(() => {
                const nav = this.$refs.paginationNav;
                if (!nav) return;
                const parent = nav.parentElement;
                if (parent) this._resizeObserver.observe(parent);
                this._resizeObserver.observe(nav);
            });
        },
        paginationAriaLabel(item) {
            if (item === 'prev') return this.$t('lbl_EMPBE_SalesLead_Pagination_Prev');
            if (item === 'next') return this.$t('lbl_EMPBE_SalesLead_Pagination_Next');
            return undefined;
        },
        paginationBtnClass(item) {
            if (item === 'ellipsis') return ['sl-pagination__btn', 'sl-pagination__btn--ellipsis'];
            if (item === 'prev' || item === 'next') return ['sl-pagination__btn', 'sl-pagination__btn--arrow'];
            if (item === this.currentPage) return ['sl-pagination__btn', 'sl-pagination__btn--active'];
            return ['sl-pagination__btn', 'sl-pagination__btn--default'];
        },
        onItemClick(item) {
            if (item === 'ellipsis') return;
            if (item === 'prev') {
                if (this.currentPage > 1) {
                    this.$emit('page-change', this.currentPage - 1);
                }
                return;
            }
            if (item === 'next') {
                if (this.currentPage < this.totalPages) {
                    this.$emit('page-change', this.currentPage + 1);
                }
                return;
            }
            if (typeof item === 'number' && item !== this.currentPage) {
                this.$emit('page-change', item);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.sl-pagination {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    flex-wrap: nowrap;
    overflow: hidden;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    margin: 0;
    padding: 0;
    align-self: stretch;
    flex-shrink: 0;
    box-sizing: border-box;
}

.sl-pagination__btn {
    flex-shrink: 0;
    box-sizing: border-box;
    width: 40px;
    height: 40px;
    padding: 8px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    background: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: Inter, sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 16px;
    color: rgba(0, 0, 0, 0.8);
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;

    &:hover:not(:disabled):not(.sl-pagination__btn--arrow) {
        background: rgba(0, 0, 0, 0.03);
    }

    &--active {
        border-color: #7366ff;
        color: #7366ff;
        cursor: default;
    }

    &--ellipsis {
        border: none;
        background: transparent;
        cursor: default;
        width: auto;
        min-width: 28px;
        padding: 8px 4px;
    }

    &--arrow {
        border: none;
        background: #7366ff;

        svg {
            pointer-events: none;
        }
    }
}

@media screen and (max-width: 480px) {
    .sl-pagination {
        justify-content: flex-start;
        gap: 6px;
        max-width: 100%;
    }

    .sl-pagination__btn {
        width: 36px;
        height: 36px;
        padding: 6px;
        font-size: 14px;
        line-height: 14px;

        &--ellipsis {
            min-width: 20px;
            padding: 6px 2px;
        }
    }
}
</style>
